import React, { useMemo } from 'react';
import {
  Box, Text,
} from '@common-fe/common-fe';

import { REGEXPS } from '@/common';
import {
  FAV_SIZE,
  LetterIconSizeEnum,
  LOGO_SIZE,
  OrganizationTypes,
} from '@/common/constants';
import colors from '@/styles/colors';

const iconShadow = (color: string) => `0px 0px 0px 6px ${color}`;

export interface Props {
  type?: OrganizationTypes,
  size: LetterIconSizeEnum,
  text?: string;
  sizePx?: string;
  testId?: string;
  isWithShadow?: boolean;
}

export const UserLetterIcon: React.FC<Props> = ({
  type,
  size,
  text,
  sizePx,
  testId,
  isWithShadow,
}) => {
  const preparedName = useMemo(() => {
    if (text && text.length > 2) {
      const splitText = text.split(' ');
      const firstName = splitText[0];
      const lastName = splitText[splitText.length - 1];

      return `${firstName} ${lastName}`;
    }

    return text;
  }, [text]);

  const firstLetters = useMemo(() => {
    if (!preparedName) return '';
    const parts = preparedName.split(' ').filter(part => part.trim() !== '');
    const letters = parts
      .map(part => part.match(REGEXPS.FIRST_LETTERS) ? part.charAt(0) : '')
      .filter(letter => letter !== '');
    return letters.join('') || '';
  }, [preparedName]);

  const backgroundByType = useMemo(() => {
    switch (type) {
    case OrganizationTypes.dependent: {
      return colors.dependent;
    }
    case OrganizationTypes.employee: {
      return colors.employee;
    }
    case OrganizationTypes.partner: {
      return colors.partner;
    }
    case OrganizationTypes.employer: {
      return colors.claimsProcessor;
    }
    case OrganizationTypes.system: {
      return colors.superAdmin;
    }
    default: {
      return undefined;
    }
    }
  }, [type]);

  const background = useMemo(
    () => backgroundByType || colors.noAssignee,
    [backgroundByType],
  );

  const boxShadow = useMemo(() => {
    if (type
      && (type === OrganizationTypes.dependent)) {
      return iconShadow(colors.dependentShadow);
    }
    if (type
      && (type === OrganizationTypes.employee)) {
      return iconShadow(colors.employeeShadow);
    }
    return undefined;
  }, [type]);

  const iconSize = useMemo(() => {
    if (sizePx) return `${sizePx}px`;
    return size === LetterIconSizeEnum.LOGO ? LOGO_SIZE : FAV_SIZE;
  }, [size, sizePx]);

  return (
    <Box
      background={background}
      justify="center"
      align="center"
      width={iconSize}
      data-testid={testId || 'LetterIcon-generate'}
      height={iconSize}
      style={{
        minWidth: iconSize,
        boxShadow: isWithShadow ? boxShadow : undefined,
        transition: 'all 0.2s ease-in-out',
      }}
      round="50%"
    >
      {
        size === LetterIconSizeEnum.LOGO
          ? (
            <Text
              weight="bold"
              size={firstLetters.length > 1 ? 'regular' : '2xl'}
              data-testid="LetterIcon-generate-logo-size"
              color="textOnColor"
            >{firstLetters}
            </Text>
          )
          : (
            <Text
              weight="bold"
              size="small"
              data-testid="LetterIcon-generate-fav-size"
              color="textOnColor"
            >{firstLetters}
            </Text>
          )
      }
    </Box>
  );
};
